<template>
    <div class="otp">
        <span class="otp__instruction" tabindex="0">
            {{ $ml.get('otpCodeSentFor') }}
            <span style="font-weight: 600;">{{ email }}</span>
        </span>
        <div class="otp__inputs-box">
            <input
                v-for="(digit, index) in otp"
                :key="index"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="1"
                class="otp__input"
                :aria-label="'Digite o dígito ' + (index + 1)"
                :class="{ 'error-border': error }"
                v-model="otp[index]"
                @input="handleInput(index)"
                @paste="handlePaste"
                @keydown="handleKeydown($event, index)"
                :ref="`otpInput${index}`"
                :disabled="isVerifying"
            />
        </div>
        <div v-if="error && attemptCount >= maxAttempts" class="otp__error-message">
            <img src="@/assets/img/icons/close-round.svg" alt="icone X" tabindex="-1" /><span v-text="$ml.get('otpAttemptsExceeded')" />
        </div>
        <button
            class="otp__resend-code"
            :disabled="isResending"
            @click="$emit('resend')"
        >
            <span v-text="$ml.get('otpResendCode')" />
        </button>
        <span class="otp__terms">
            <span v-text="$ml.get('otpEmailConfirmation')" />
            <a :href="showTerms" target="_blank">
                <span v-text="$ml.get('otpTerms')" />
            </a>.
        </span>

        <div v-if="isVerifying" class="otp__loading">
            <ClipLoader :loading="true" color="#0774E7" />
        </div>
    </div>
</template>

<script>
import vars from '@/config/vars';
import ClipLoader from 'vue-spinner/src/ClipLoader';

export default {
	components: {
		ClipLoader
	},
    props: {
        email: {
            type: String,
            required: true
        },
		isVerifying: {
			type: Boolean,
			default: false,
    	},
		error: {
			type: Boolean,
			default: false,
		},
		attemptCount: {
			type: Number,
			required: true,
		},
		maxAttempts: {
			type: Number,
			required: true,
		},
    },
    data() {
        return {
			showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
            otp: Array(6).fill(''),
			isResending: false,
        };
    },
	mounted() {
		this.focusInput(0);
	},
    methods: {
       handleInput(index) {
            this.otp[index] = this.otp[index].replace(/[^0-9]/g, '');
            if (this.otp[index] && index < this.otp.length - 1) {
                this.focusInput(index + 1);
            }
            if (this.isOtpComplete()) {
                this.$emit('verify', this.otp.join(''));
            }
        },
        handlePaste(event) {
            event.preventDefault();
            const pastedText = event.clipboardData.getData('text').replace(/\D/g, '');
            pastedText.split('').forEach((char, i) => {
                if (i < this.otp.length) {
                    this.$set(this.otp, i, char);
                }
            });
            this.focusInput(Math.min(pastedText.length, this.otp.length) - 1);
            if (this.isOtpComplete()) {
                this.$emit('verify', this.otp.join(''));
            }
        },
        handleKeydown(event, index) {
            if (event.key === 'Backspace' && !this.otp[index] && index > 0) {
                this.focusInput(index - 1);
            }
        },
		clearOtp() {
            this.otp = Array(6).fill('');
            this.focusInput(0);
        },
		focusInput(index) {
			this.$nextTick(() => {
				const ref = this.$refs[`otpInput${index}`];
				const input = ref && ref[0];
				if (input) input.focus();
			});
		},
        isOtpComplete() {
            return this.otp.every(digit => digit !== '');
        },
    },
};
</script>

<style lang="scss" scoped>
.otp {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    padding-bottom: 24px;

    &__instruction {
		width: 100%;
		max-width: 328px;
        font-size: 16px;
		font-weight: 500;
        margin-bottom: 20px;
        color: #454F5C;

        @media screen and (min-width: 768px) {
			width: 100%;
			max-width: initial;
        }
    }

    &__inputs-box {
        display: flex;
        flex-direction: row;
        margin-bottom: 20px;
        width: 100%;
		gap: 8px;

        @media screen and (max-width: 480px) {
            justify-content: space-between;
        }
    }

    &__input {
		display: flex;
		justify-content: center;
		align-items: center;
        width: 48px;
        height: 56px;
        padding: 20px 12px;
        border-radius: 12px;
        font-size: 16px;
		font-weight: 500;
		color: #2F363E;
		text-align: center;
        border: 1.5px solid #B8C1CC;
        outline: none;
        transition: border-color 0.2s;

        &:focus {
			border: 2.5px solid #0774E7;
        }

        &.error-border {
			border: 2.5px solid #DC392A;
        }

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

		&:disabled {
			background-color: #EFF1F3;
			border: 1.5px solid #B8C1CC;
			cursor: not-allowed;
		}
    }

    &__error-message {
        width: 100%;
		display: flex;
        color:#000000;
        font-size: 14px;
		font-weight: 500;
		padding: 12px;
        margin-bottom: 20px;
		background-color: #FEE4E2;
		border-radius: 12px;

		span {
			display: block;
			width: 100%;
			max-width: 272px;
			margin-left: 8px;

			@media screen and (min-width: 768px) {
			max-width: initial;
        }

		}

        @media screen and (min-width: 768px) {
			max-width: initial;
            margin-bottom: 12px;
        }
    }

    &__resend-code {
        background: none;
        color: #0774E7;
        cursor: pointer;
        padding: 0;
        border: none;
        font-size: 16px;
		font-weight: 600;
		text-decoration: underline;
		margin-bottom: 20px;
    }

    &__terms {
		display: block;
		width: 100%;
		max-width: 328px;
        font-size: 16px;
		font-weight: 500;
        color: #454F5C;
		margin-bottom: 24px;

        @media screen and (min-width: 768px) {
			max-width: initial;
			margin-bottom: 20px;
        }

        a {
			font-size: 16px;
			font-weight: 600;
			margin-left: 5px;
            color: #007bff;
            text-decoration: underline;
        }
    }

	&__loading {
		padding: 40px;
		margin: 0 auto;

		@media screen and (min-width: 768px) {
			padding: 0;
        }
	}
}
</style>
